import { Injectable, Injector, inject, runInInjectionContext } from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {AiChatEvent} from './ai-chat-event.model';
import {AiGlobalChatConfig} from './ai-global-chat-config.model';
import {filter} from 'rxjs/operators';
import {CourseSection} from '../models/course-section.model';
import {Lesson} from '../models/lesson.model';
import {createSelector, select, Store} from '@ngrx/store';
import {
  selectActiveCourse,
  selectActiveCourseAllLessons,
  selectActiveCourseDescription,
  selectActiveCourseSections
} from '../store/selectors';
import {AppState} from '../store';
import {CoursePromptParams, CoursePromptSectionParams} from './course-prompt-params.model';
import {Course} from '../models/course.model';
import {extractTextContentFromHTML} from '../common/generic-utils';
import { collection, getDocs, Firestore, limit, orderBy, query, where } from '@angular/fire/firestore';

const aiChatDataSelector = createSelector(
  selectActiveCourse,
  selectActiveCourseAllLessons,
  selectActiveCourseSections,
  selectActiveCourseDescription,
  (course, lessons, sections, description) => {
    return {
      course,
      lessons,
      sections,
      description
    };
  }
);

@Injectable({
  providedIn: 'root'
})
export class AiGlobalChatService {
  private firestore = inject(Firestore);
  private injector = inject(Injector);
  private store = inject<Store<AppState>>(Store);


  private subject = new BehaviorSubject<AiChatEvent>(null);

  showGlobalAiChat$ = this.subject.asObservable().pipe(filter(val => !!val));

  /** Inserted by Angular inject() migration for backwards compatibility */
  constructor(...args: unknown[]);

  constructor() {
  }

  private dispatchGlobalAiChat(aiChatEvent: AiChatEvent) {
    this.subject.next(aiChatEvent);
  }

  toggleAiChat() {
    if (this.subject?.value?.show) {
      this.endGlobalAiChatSession();
    } else {
      this.startDefaultAiChatSession();

    }
  }

  startGlobalAiChatSession(config: AiGlobalChatConfig) {
    // document.body.style.overflow = 'hidden';
    this.dispatchGlobalAiChat({
      show: true,
      newSession: true,
      config
    });
  }

  endGlobalAiChatSession() {
    document.body.style.overflow = 'visible';
    this.dispatchGlobalAiChat({
      show: false,
      newSession: false
    });
    // window.scrollTo(0, 0);
  }

  async getAiChatDetail(tenantId: string, aiChatSessionId: string): Promise<any> {
    const path = `aiChatSessions/${tenantId}/chats`;

    const constraints = [
      where('aiChatSessionId', '==', aiChatSessionId),
      orderBy('createdAt', 'desc')
    ];

    const snapshot = await runInInjectionContext(this.injector, () =>
      getDocs(query(collection(this.firestore, path), ...constraints))
    );
    return snapshot.docs.map(d => ({ id: d.id, ...d.data() as any }));
  }

  async getAiChatSummary(tenantId: string): Promise<any> {
    const path = `aiChatSessions/${tenantId}/chats`;

    const constraints = [
      orderBy('createdAt', 'desc'),
      limit(50)
    ];

    const snapshot = await runInInjectionContext(this.injector, () =>
      getDocs(query(collection(this.firestore, path), ...constraints))
    );
    const selectedFields = ['sessionTitle', 'createdAt'];

    return snapshot.docs.map(d => {
      const data = d.data() as any;
      const selectedData: any = {};

      selectedFields.forEach((field) => {
        if (data.hasOwnProperty(field)) {
          selectedData[field] = data[field];
        }
      });

      return { aiChatSessionId: d.id, ...selectedData };
    });
  }

  startDefaultAiChatSession() {
    this.startGlobalAiChatSession({
      promptId: 'defaultAssistant'
    });
  }

  getSectionsLessonsTitle() {
    return this.store.pipe(select(aiChatDataSelector));
  }

  stringifySectionsAndLessons(sections: CourseSection[], lessons: Lesson[]): any {
    const result = [];

    for (const section of sections) {
      const sectionTitle = section.title;
      const lessonsBySection = lessons.filter((lesson) => lesson.sectionId === section.id);

      if (lessonsBySection.length > 0) {
        const res = [];
        result.push({
          sectionTitle,
          lessons: lessonsBySection.map((lesson) => lesson.title),
        });
      }
    }

    let str = '';
    for (const lesson of result) {
      const les = lesson.lessons.join(', ');
      str += ` ${lesson.sectionTitle}: ${les};`;
    }

    return str;
  }

  removeHtmlTags(input: string): string {
    return input?.replace(/<[^>]+>/g, '\n');
  }

  prepareCoursePromptParams(course: Course, sections: CourseSection[], lessons: Lesson[], courseDescription?:string): CoursePromptParams {

    const coursePromptParams: CoursePromptParams = {
      title: course.title,
      sections: [],
      courseDescription: extractTextContentFromHTML(courseDescription)
    };

    if (sections?.length) {
      for (const section of sections) {

        const sectionTitle = section.title;

        const sectionParams: CoursePromptSectionParams = {
          title: sectionTitle,
          lessons: []
        }

        coursePromptParams.sections.push(sectionParams);

     if(lessons?.length){
      const lessonsBySection = lessons.filter((lesson) => lesson.sectionId === section.id);

      const lessonsPromptParams = lessonsBySection.map((lesson) => {
        return {
          title: lesson.title
        }
      });

      sectionParams.lessons = lessonsPromptParams;
     }
      }
    }

    return coursePromptParams;
  }

  prepareLessonPromptParams(course: Course, sections: CourseSection[], lessons: Lesson[], lesson: Lesson, lessonDescription?:string) {

    const promptParams = this.prepareCoursePromptParams(course, sections, lessons) as any;

    promptParams.lessonTitle = lesson?.title;

    if (lessonDescription) {
      promptParams.lessonDescription = extractTextContentFromHTML(lessonDescription);
    }

    return promptParams;
  }

}
